import _ from "lodash"
import { LogDebug, LogError, LogInfo } from "../../../../../../helpers/logger"
import { updateOrdering } from "../../helpers/dndElementHelper"
import { GetNewFormulaOrder } from "../formulaBuilderHelpers"
import formulaBuilderTypes from "../formulaBuilderTypes"
import {
  functionAcceptsMoreValues,
  functionTypeAcceptsQuestionType,
} from "./formulaHelpers"
import { defaultNewItemId as defaultFormulaNewItemId } from "../RenderFormula/RenderFormulaNewItem"
import elementTypes from "../../Elements/elementTypes"

export function dndDragOverFormulaBuilder(
  draggingObject,
  formulasList,
  defaultOperator
) {
  if (
    [
      formulaBuilderTypes.formulaTypes.question,
      formulaBuilderTypes.formulaTypes.number,
      formulaBuilderTypes.formulaTypes.group,
      formulaBuilderTypes.draggableTypes.function,
      formulaBuilderTypes.formulaTypes.option,
    ].includes(draggingObject.type)
  ) {
    let newFormulas = _.cloneDeep(formulasList)

    const newFormulaOrder = GetNewFormulaOrder(
      draggingObject.type,
      newFormulas.length,
      draggingObject.object,
      defaultOperator
    )

    //change the last formulaOrder entry to the current default
    const maxActiveOrderNumber = Math.min(
      ...newFormulas
        .filter((formulaOrder) => !formulaOrder.isDeleted)
        .map((formulaOrder) => formulaOrder.orderNumber)
    )
    let lastFormula = newFormulas.find(
      (formulaOrder) =>
        formulaOrder.orderNumber === maxActiveOrderNumber &&
        !formulaOrder.isDeleted
    )

    if (lastFormula?.function) {
      lastFormula.function.connectingOperator = defaultOperator
    } else if (lastFormula?.group) {
      lastFormula.group.connectingOperator = defaultOperator
    } else if (lastFormula?.value) {
      lastFormula.value.connectingOperator = defaultOperator
    }

    newFormulas = [...newFormulas, newFormulaOrder]

    return { newFormulas, newFormulaOrder }
  } else {
    LogDebug("Dropped a non-formula item on the formula builder")
    return undefined
  }
}

export function dndDragOverGroup(
  draggingObject, //The object being dragged
  overGroupId, //The group that the object is dragged over
  currentFormulaOrder, //The current formulaOrder associated with the dragged object
  formulas,
  defaultOperator
) {
  if (!["operator"].includes(draggingObject.type)) {
    let useFormulas = _.cloneDeep(formulas)
    let useFormulaOrder = undefined
    const overGroupIdClean = overGroupId.replace("FormulaGroup_", "")

    if (currentFormulaOrder) {
      useFormulaOrder = _.cloneDeep(currentFormulaOrder)
    } else {
      GetNewFormulaOrder(
        draggingObject.type,
        useFormulas.length,
        draggingObject.object,
        defaultOperator
      )
    }

    const groupFormulaOrder = useFormulas.find(
      (formulaOrder) => formulaOrder?.group?.id === overGroupIdClean
    )

    if (
      groupFormulaOrder?.group?.formulas?.some(
        (formula) => formula.id === draggingObject.formulaOrder?.id
      )
    ) {
      LogInfo("Already added")
    } else {
      if (
        useFormulaOrder &&
        !useFormulaOrder.group &&
        !groupFormulaOrder.group?.formulas?.some(
          (formula) => formula.id === useFormulaOrder.id
        )
      ) {
        //Set the groupId
        useFormulaOrder.groupId = groupFormulaOrder.group.id
        //Add the formulaOrder to the group
        groupFormulaOrder.group.formulas = [
          ...groupFormulaOrder?.group?.formulas,
          useFormulaOrder,
        ]
        groupFormulaOrder.group.formulas = updateOrdering(
          groupFormulaOrder.group.formulas
        )

        //Remove the formulaOrder from the main formula order
        useFormulas = useFormulas.filter(
          (formula) => formula.id !== useFormulaOrder.id
        )
        useFormulas = updateOrdering(useFormulas)
      }
    }

    return {
      newFormulas: useFormulas,
      newFormulaOrder: useFormulaOrder,
      activeGroup: groupFormulaOrder,
    }
  }
}

export function dndRemoveFromGroup(
  currentFormulaOrder, //The current formulaOrder associated with the dragged object
  Formulas
) {
  let useFormulas = _.cloneDeep(Formulas)

  //Find the group
  const groupFormulaOrder = useFormulas.find(
    (formulaOrder) => formulaOrder?.group?.id === currentFormulaOrder.groupId
  )

  //Remove the formulaOrder
  groupFormulaOrder.group.formulas = groupFormulaOrder.group.formulas.filter(
    (formula) => formula.id !== currentFormulaOrder.id
  )
  groupFormulaOrder.group.formulas = updateOrdering(
    groupFormulaOrder.group.formulas
  )

  currentFormulaOrder.groupId = undefined

  //Put the formula Order in the main form
  useFormulas = [...useFormulas, currentFormulaOrder]
  useFormulas = updateOrdering(useFormulas)

  return { newFormulas: useFormulas, activeGroup: groupFormulaOrder }
}

export function dndDragOverConnector(operator, connectorId, formulas) {
  let useFormulas = _.cloneDeep(formulas)

  //retrieve the formulaOrderId
  const formulaOrderId = connectorId.replace(
    formulaBuilderTypes.droppableIds.connector,
    ""
  )

  if (formulaOrderId === defaultFormulaNewItemId) {
    LogDebug("dndDragOverConnector: Ignoring new item connector")
    return
  }

  let formula = useFormulas.find((formulaOrder) => {
    if (formulaOrder.id === formulaOrderId) {
      return true
    } else if (formulaOrder.group) {
      return formulaOrder?.group?.formulas?.some(
        (formula) => formula.id === formulaOrderId
      )
    }

    return false
  })

  //If this is a group and the ID doesn't match, look in the group formulas
  if (formula?.group && formula.id !== formulaOrderId) {
    formula = formula?.group?.formulas?.find(
      (formula) => formula.id === formulaOrderId
    )
  }

  if (formula) {
    if (formula.function) {
      formula.function.connectingOperator = operator.id
    } else if (formula.value) {
      formula.value.connectingOperator = operator.id
    } else if (formula.group) {
      formula.group.connectingOperator = operator.id
    } else if (formula.question) {
      formula.question.connectingOperator = operator.id
    } else if (formula.option) {
      formula.option.connectingOperator = operator.id
    } else {
      LogError("dndDragOverConnector: Unknown formulaOrder type")
      return
    }

    return useFormulas
  } else {
    LogError("dndDragOverConnector: Unable to find formulaOrder")
  }
}

export function dndDragOverFunction(
  draggingObject, //The object being dragged
  overGroupId, //The group that the object is dragged over
  currentFormulaOrder, //The current formulaOrder associated with the dragged object
  formulas,
  defaultOperator
) {
  try {
    if (!["operator", "formula"].includes(draggingObject.type)) {
      let useFormulas = _.cloneDeep(formulas)
      let useFormulaOrder = undefined
      const overFunctionIdClean = overGroupId.replace("FormulaFunction_", "")

      if (currentFormulaOrder) {
        useFormulaOrder = _.cloneDeep(currentFormulaOrder)
      } else {
        GetNewFormulaOrder(
          draggingObject.type,
          useFormulas.length,
          draggingObject.object,
          defaultOperator
        )
      }

      if (currentFormulaOrder?.functionId) {
        const result = dndRemoveFromFunction(
          currentFormulaOrder,
          useFormulas,
          true
        )
        useFormulas = _.cloneDeep(result?.newFormulas)
      }

      //Find the function that we want to add to
      let functionFormulaOrder = undefined

      const objectFound = useFormulas.find((formulaOrder) => {
        if (formulaOrder.group) {
          //If this is a group, check its formulas
          const found = formulaOrder?.group?.formulas.find(
            (formula) => formula.function?.id === overFunctionIdClean
          )

          if (found) {
            functionFormulaOrder = found
            return true
          }
        } else if (formulaOrder?.function?.id === overFunctionIdClean) {
          functionFormulaOrder = formulaOrder
          return true
        } else {
          return false
        }
      })

      if (!objectFound) {
        LogError("dndDragOverFunction: Unable to find function formulaOrder")
        return null
      }

      //Find the group that the function is in
      const groupOrder = useFormulas.find((formulaOrder) => {
        if (formulaOrder.group) {
          return formulaOrder?.group?.id === functionFormulaOrder?.groupId
        }
        return false
      })

      const objectType = currentFormulaOrder?.option
        ? elementTypes.optionElementId
        : currentFormulaOrder?.question?.type

      if (
        !functionTypeAcceptsQuestionType(
          functionFormulaOrder?.function?.type,
          objectType
        ) &&
        !currentFormulaOrder?.value
      ) {
        LogDebug("Function does not accept this question type")
      } else if (
        !functionAcceptsMoreValues(functionFormulaOrder, currentFormulaOrder)
      ) {
        LogDebug("Function cannot accept more values")
      } else if (
        functionFormulaOrder?.function?.values?.some(
          (value) => value.id === draggingObject.formulaOrder?.id
        )
      ) {
        LogDebug("Already added")
      } else {
        if (
          useFormulaOrder &&
          !useFormulaOrder?.function &&
          !functionFormulaOrder?.function?.values?.some(
            (value) => value.id === useFormulaOrder.id
          )
        ) {
          //Set the functionId
          useFormulaOrder.functionId = functionFormulaOrder.function.id
          //Add the formulaOrder to the formula values
          functionFormulaOrder.function.values = [
            ...functionFormulaOrder?.function?.values,
            useFormulaOrder,
          ]
          functionFormulaOrder.function.values = updateOrdering(
            functionFormulaOrder.function.values
          )

          //If the function is in a group, remove the newly dragged in formulaOrder from the group
          if (groupOrder) {
            groupOrder.group.formulas = groupOrder.group.formulas.filter(
              (formula) => formula.id !== useFormulaOrder.id
            )
          }

          //Remove the formulaOrder from the main formula order
          useFormulas = useFormulas.filter(
            (formula) => formula.id !== useFormulaOrder.id
          )
          useFormulas = updateOrdering(useFormulas)
        }
      }

      return {
        newFormulas: useFormulas,
        newFormulaOrder: useFormulaOrder,
        activeFunction: functionFormulaOrder,
      }
    }
  } catch (error) {
    LogError("Error in dndDragOverFunction:", error)
    return null
  }
}

export function dndRemoveFromFunction(
  currentFormulaOrder, //The current formulaOrder associated with the dragged object
  Formulas,
  ignoreGroupSetup = false //If true, the function will not try to put the formulaOrder back in the group
) {
  try {
    let useFormulas = _.cloneDeep(Formulas)

    //Find the function
    let functionFormulaOrder = undefined

    const objectFound = useFormulas.find((formulaOrder) => {
      if (formulaOrder.group) {
        //If this is a group, check its formulas
        const found = formulaOrder?.group?.formulas.find(
          (formula) => formula.function?.id === currentFormulaOrder.functionId
        )

        if (found) {
          functionFormulaOrder = found
          return true
        }
      } else if (
        formulaOrder?.function?.id === currentFormulaOrder.functionId
      ) {
        functionFormulaOrder = formulaOrder
        return true
      } else {
        return false
      }
    })

    if (!objectFound) {
      LogError("dndRemoveFromFunction: Unable to find function formulaOrder")
      return null
    }

    const groupFormulaOrder = functionFormulaOrder?.groupId
      ? useFormulas.find((formulaOrder) => {
          if (formulaOrder?.group?.id === functionFormulaOrder?.groupId) {
            return formulaOrder?.group?.id === functionFormulaOrder?.groupId
          }

          return false
        })
      : undefined

    //Remove the formulaOrder
    functionFormulaOrder.function.values =
      functionFormulaOrder.function.values.filter(
        (formula) => formula.id !== currentFormulaOrder.id
      )
    functionFormulaOrder.function.values = updateOrdering(
      functionFormulaOrder.function.values
    )

    currentFormulaOrder.functionId = undefined

    if (groupFormulaOrder && !ignoreGroupSetup) {
      //If the function is in a group, put the formulaOrder back in the group
      groupFormulaOrder.group.formulas = [
        ...groupFormulaOrder?.group?.formulas,
        currentFormulaOrder,
      ]
      groupFormulaOrder.group.formulas = updateOrdering(
        groupFormulaOrder.group.formulas
      )
    } else {
      //Put the formula Order in the main form
      useFormulas = [...useFormulas, currentFormulaOrder]
      useFormulas = updateOrdering(useFormulas)
    }

    return {
      newFormulas: useFormulas,
      activeGroup: groupFormulaOrder,
    }
  } catch (error) {
    LogError("Error in dndRemoveFromFunction:", error)
    return null
  }
}
